import { Box, Button, InputLabel, Stack, Typography } from "@mui/material";
import ModalContainer from "../UIElements/modal-container";
import { useTheme } from "@mui/material/styles";
import { labelSty, labelStyles, textSty } from "../UIElements/styles-input";
import { LoadingButton } from "@mui/lab";
import { useMutation } from "@tanstack/react-query";
import { apiVerifyFacilityById } from "../../api/facility";
import { useNotificationService } from "../notification";
import {
  extractContractDates,
  formatDateFromString,
  formatTextType,
  getUrlPathId,
} from "../../utils/url-id";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import useResponsive from "../../hooks/useResponsive";
import { headerSty } from "../table-styles";
import { PATH_HOSPITAL } from "../../routes/path";

export const VerifyHospitalModal = ({
  open,
  modalHandler,
}: {
  open: boolean;
  modalHandler: () => void;
}) => {
  const theme = useTheme();
  const { showNotification } = useNotificationService();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();

  const mutation = useMutation({
    mutationFn: (field: { token: string; facilityId: string }) =>
      apiVerifyFacilityById(field),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        showNotification(data?.data?.message ?? "Success");
        navigate(PATH_HOSPITAL.root);
      }
    },
    onError: (error) => {
      console.error("Fetch process failed", error);
      showNotification("Fetch process failed", "error");
    },
  });
  const handleVerify = () => {
    const facilityId = getUrlPathId(location.pathname);
    mutation.mutate({ token, facilityId });
  };
  return (
    <ModalContainer
      sx={{
        borderRadius: "16px",
      }}
      size={"xs"}
      openModal={open}
      fullWidth={true}
      handleModal={() => {}}
    >
      <Box sx={{ background: "#fff", padding: 3, borderRadius: "16px" }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Outfit",
            fontWeight: 500,
            color: "#292925",
          }}
        >
          Verify Hospital account
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: "Outfit",
              fontWeight: 500,
              color: "#292925",
            }}
          >
            Kindly click the verify button below to verify facility
          </Typography>
          <Stack
            direction={"row"}
            justifyContent={"space-around"}
            sx={{ gap: 3, mt: 3 }}
          >
            <Button
              onClick={modalHandler}
              sx={{
                width: 150,
                borderRadius: "12px",
                background: theme.palette.lightBlue.light,
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <LoadingButton
              loading={mutation.isPending}
              onClick={handleVerify}
              sx={{ width: 150, borderRadius: "12px", textTransform: "none" }}
              variant="contained"
            >
              Verify
            </LoadingButton>
          </Stack>
        </Box>
      </Box>
    </ModalContainer>
  );
};

export const EmploymentDetailsModal = ({
  open,
  modalHandler,
  employmentData,
}: {
  open: boolean;
  modalHandler: () => void;
  employmentData: any;
}) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");
  return (
    <ModalContainer
      sx={{
        borderRadius: "16px",
      }}
      size={"md"}
      openModal={open}
      fullWidth={true}
      handleModal={() => {}}
    >
      <Box sx={{ background: "#fff", padding: 4, borderRadius: "16px" }}>
        <Box>
          <Typography
            sx={{ ...headerSty, textAlign: "left", my: 2, color: "black" }}
            variant="subtitle2"
          >
            Medic Info
          </Typography>
          <Box>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Medic Name
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {employmentData?.medic_info?.name}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Medic Type
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(employmentData?.medic_info?.medic_type)}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/*  */}
          <Box>
            <Typography
              sx={{
                ...headerSty,
                textAlign: "left",
                my: 0,
                mb: 2,
                color: "black",
              }}
              variant="subtitle2"
            >
              Employment Information
            </Typography>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Job Title
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(employmentData?.job_info?.job_title)}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>Job Type</Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(employmentData?.job_info?.job_type)}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Department
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(employmentData?.job_info?.department)}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Employment Type
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(employmentData?.job_info?.employment_type)}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Work location
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(employmentData?.job_info?.work_location)}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Work Type
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(employmentData?.job_info?.work_type)}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Contract Duration
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {extractContractDates(
                    employmentData?.job_info?.contract_duration
                  )}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>Status</Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(employmentData?.status)}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Box>
        <Stack
          direction={"row"}
          justifyContent={"space-around"}
          sx={{ gap: 3, mt: 3 }}
        >
          <Button
            onClick={modalHandler}
            sx={{
              width: 150,
              borderRadius: "4px",
              background: theme.palette.lightBlue.light,
            }}
            variant="outlined"
          >
            Close
          </Button>
        </Stack>
      </Box>
    </ModalContainer>
  );
};

export const JobDetailsModal = ({
  open,
  modalHandler,
  jobData,
}: {
  open: boolean;
  modalHandler: () => void;
  jobData: any;
}) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");

  return (
    <ModalContainer
      sx={{
        borderRadius: "16px",
      }}
      size={"md"}
      openModal={open}
      fullWidth={true}
      handleModal={() => {}}
    >
      <Box sx={{ background: "#fff", padding: 4, borderRadius: "16px" }}>
        <Box>
          <Typography
            sx={{ ...headerSty, textAlign: "left", my: 2, color: "black" }}
            variant="subtitle2"
          >
            Job Information
          </Typography>
          <Box>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Job Title
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {jobData?.job_title}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>Job Type</Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(jobData.job_type)}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Job Description
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {jobData?.job_description}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Qualifications
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(jobData.qualifications)}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Department
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {jobData?.department}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Employment Type
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(jobData.employment_type)}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Experience Level
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {jobData?.experience_level}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Application Deadline
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(jobData.application_deadline)}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/*  */}
          <Box>
            <Typography
              sx={{
                ...headerSty,
                textAlign: "left",
                my: 0,
                mb: 2,
                color: "black",
              }}
              variant="subtitle2"
            >
              Facility Information
            </Typography>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Facility name
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {jobData?.facility_info?.name}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Work Location
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {jobData?.work_location}
                </Typography>
              </Stack>
            </Stack>

            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Work Type
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(jobData.work_type)}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Work Schedule
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(jobData?.work_schedule)}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Contract Duration
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {extractContractDates(jobData?.contract_duration)}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>Status</Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(jobData?.status)}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Box>
        <Stack
          direction={"row"}
          justifyContent={"space-around"}
          sx={{ gap: 3, mt: 3 }}
        >
          <Button
            onClick={modalHandler}
            sx={{
              width: 150,
              borderRadius: "4px",
              background: theme.palette.lightBlue.light,
            }}
            variant="outlined"
          >
            Cancel
          </Button>
        </Stack>
      </Box>
    </ModalContainer>
  );
};
